import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StorefrontLayout from '../components/layout/StorefrontLayout';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const message =
          (body as { message?: string }).message
          || t('auth.forgot_error', 'Unable to send reset email.');
        throw new Error(message);
      }
      setSuccess(t('auth.forgot_success', 'If an account exists, a reset link has been sent.'));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('auth.forgot_error', 'Unable to send reset email.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StorefrontLayout contentContained mainClassName="bg-slate-50">
      <div className="flex flex-1 items-center justify-center py-10">
        <section className="glass-panel w-full max-w-md p-6 sm:p-8">
          <h1 className="mb-2 text-xl font-semibold tracking-tight text-slate-900">
            {t('auth.forgot_title', 'Reset your password')}
          </h1>
          <p className="mb-4 text-sm text-slate-600">
            {t(
              'auth.forgot_subtitle',
              'Enter your email address and we will send you a link to reset your password.',
            )}
          </p>
          {error && (
            <p className="mb-2 text-xs text-red-500">
              {error}
            </p>
          )}
          {success && (
            <p className="mb-2 text-xs text-emerald-600">
              {success}
            </p>
          )}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5 text-sm">
              <label
                className="block text-xs font-medium text-slate-700"
                htmlFor="forgotEmail"
              >
                {t('auth.email_label')}
              </label>
              <input
                id="forgotEmail"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/70"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center rounded-md bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/80 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting
                ? t('auth.forgot_sending', 'Sending…')
                : t('auth.forgot_submit', 'Send reset link')}
            </button>
          </form>
        </section>
      </div>
    </StorefrontLayout>
  );
}

export default ForgotPasswordPage;

